import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat, Field } from "@/pages/admin/_shared";
import {
  ShieldCheck, Key, Devices, Warning, Lock, Trash, Plus, Check, X, MapPin
} from "@phosphor-icons/react";

export default function AdminSecurity() {
  const [licenses, setLicenses] = useState([]);
  const [devices, setDevices] = useState([]);
  const [logins, setLogins] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [l, d, e, s, u] = await Promise.all([
      api.get("/admin/licenses"),
      api.get("/admin/security/devices"),
      api.get("/admin/security/login-events"),
      api.get("/admin/security/stats"),
      api.get("/admin/users?limit=20"),
    ]);
    setLicenses(l.data); setDevices(d.data); setLogins(e.data); setStats(s.data); setUsers(u.data.items);
  };
  useEffect(() => { load(); }, []);

  const genLicense = async (e) => {
    e.preventDefault();
    if (!userId) return;
    setBusy(true);
    try {
      await api.post("/admin/licenses", { user_id: userId, max_devices: 3 });
      load();
    } finally { setBusy(false); }
  };
  const revokeL = async (id) => { await api.patch(`/admin/licenses/${id}/revoke`); load(); };
  const revokeDev = async (id) => { await api.post(`/admin/security/devices/${id}/revoke`); load(); };
  const deleteL = async (id) => {
    if (!window.confirm("Delete this license?")) return;
    await api.delete(`/admin/licenses/${id}`);
    load();
  };

  if (!stats) return <div className="p-10 text-ink-dim">LOADING SECURITY…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-security">
      <AdminHeader section="SECURITY & LICENSING" title={["LOCK IT ", "*DOWN."]} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="ACTIVE LICENSES" value={stats.active_licenses} Icon={Key} color="text-brand-green" testid="sec-stat-licenses" />
        <Stat label="REVOKED" value={stats.revoked_licenses} testid="sec-stat-revoked" />
        <Stat label="ACTIVATED DEVICES" value={stats.total_devices} Icon={Devices} color="text-brand-purple" testid="sec-stat-devices" />
        <Stat label="SUSPICIOUS / 24H" value={stats.suspicious_logins_24h} Icon={Warning} color="text-brand-red" testid="sec-stat-suspicious" />
        <Stat label="PIRACY BLOCKED" value={stats.piracy_blocked_24h} Icon={Lock} color="text-brand-orange" testid="sec-stat-piracy" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* License generator */}
        <Panel title="GENERATE LICENSE" testid="sec-license-form">
          <form onSubmit={genLicense} className="space-y-3">
            <label className="block">
              <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">Issue to user</span>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                data-testid="sec-user-select"
                className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm font-mono outline-none focus:border-brand-red"
              >
                <option value="">— SELECT USER —</option>
                {users.map((u) => (
                  <option
                    key={u.id}
                    value={u.id}
                    label={`${u.producer_name} (${u.email})`}
                  />
                ))}
              </select>
            </label>
            <button type="submit" disabled={busy || !userId} data-testid="sec-generate-btn" className="w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Plus size={12} weight="bold" /> {busy ? "GENERATING…" : "ISSUE LICENSE KEY"}
            </button>
            <p className="text-[10px] text-ink-dim leading-relaxed pt-2 border-t border-zinc-900">
              Keys can be activated on up to 3 devices. Anti-piracy fingerprinting active.
            </p>
          </form>

          <div className="mt-5 pt-4 border-t border-zinc-900">
            <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mb-2">TWO-FACTOR AUTH</div>
            <div className="flex items-center justify-between">
              <span className="text-xs">Enabled across base</span>
              <span className="font-display font-black text-brand-green">{stats.two_factor_enabled_pct}%</span>
            </div>
            <div className="h-1.5 bg-zinc-900 mt-2">
              <div className="h-full bg-brand-green" style={{ width: `${stats.two_factor_enabled_pct}%`, boxShadow: "0 0 10px #00FF66" }} />
            </div>
          </div>
        </Panel>

        {/* Licenses list */}
        <Panel title="LICENSE KEYS" tag={`${licenses.length}`} className="lg:col-span-2" testid="sec-licenses">
          {licenses.length === 0 ? (
            <div className="text-xs text-ink-dim text-center py-10">No licenses yet.</div>
          ) : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto">
              {licenses.map((l) => (
                <li key={l.id} className={`border p-3 bg-black/40 ${l.status === "revoked" ? "border-zinc-900 opacity-60" : "border-zinc-900"}`} data-testid={`sec-license-${l.id}`}>
                  <div className="flex items-center gap-3">
                    <Key size={16} weight="fill" className={l.status === "active" ? "text-brand-green" : "text-ink-dim"} />
                    <div className="flex-1 min-w-0">
                      <code className="text-xs text-white font-mono block truncate">{l.key}</code>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-1">
                        {l.user_name} · {l.active_devices}/{l.max_devices} devices
                      </div>
                    </div>
                    <span className={`text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 border ${
                      l.status === "active" ? "border-brand-green text-brand-green" : "border-zinc-700 text-ink-dim"
                    }`}>{l.status}</span>
                    {l.status === "active" && (
                      <button onClick={() => revokeL(l.id)} data-testid={`sec-revoke-${l.id}`} className="text-ink-mute hover:text-brand-red p-1" title="Revoke">
                        <Lock size={12} weight="bold" />
                      </button>
                    )}
                    <button onClick={() => deleteL(l.id)} data-testid={`sec-delete-${l.id}`} className="text-ink-mute hover:text-brand-red p-1" title="Delete">
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Devices + login events */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="ACTIVE DEVICES" tag={`${devices.length}`} testid="sec-devices">
          {devices.length === 0 ? (
            <div className="text-xs text-ink-dim text-center py-10">No devices registered.</div>
          ) : (
            <ul className="space-y-2 max-h-[360px] overflow-y-auto">
              {devices.map((d) => (
                <li key={d.id} className={`border border-zinc-900 p-3 bg-black/40 flex items-center gap-3 ${d.status === "revoked" ? "opacity-60" : ""}`} data-testid={`sec-device-${d.id}`}>
                  <Devices size={18} weight="bold" className={d.platform === "macos" ? "text-white" : "text-brand-cyan"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold tracking-tight text-sm truncate">{d.device_name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-0.5">
                      {d.platform.toUpperCase()} · {d.cpu} · {d.ip}
                    </div>
                  </div>
                  {d.status === "active" ? (
                    <button onClick={() => revokeDev(d.id)} data-testid={`sec-device-revoke-${d.id}`} className="text-[9px] tracking-[0.3em] uppercase border border-brand-red text-brand-red px-2 py-1 hover:bg-brand-red hover:text-black">
                      REVOKE
                    </button>
                  ) : (
                    <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">REVOKED</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="LOGIN EVENTS · SUSPICIOUS DETECTION" tag={`${logins.filter(l => l.suspicious).length} ALERTS`} testid="sec-logins">
          {logins.length === 0 ? (
            <div className="text-xs text-ink-dim text-center py-10">No login events.</div>
          ) : (
            <ul className="space-y-2 max-h-[360px] overflow-y-auto">
              {logins.map((l) => (
                <li key={l.id} className={`border p-3 ${l.suspicious ? "border-brand-red/40 bg-brand-red/5" : "border-zinc-900 bg-black/40"}`} data-testid={`sec-login-${l.id}`}>
                  <div className="flex items-center gap-3">
                    {l.suspicious ? <Warning size={16} weight="fill" className="text-brand-red animate-flicker" /> : <Check size={16} weight="bold" className="text-brand-green" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold tracking-tight text-sm truncate">{l.producer_name}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-0.5 flex items-center gap-2">
                        <MapPin size={10} weight="fill" /> {l.location} · {l.ip}
                      </div>
                    </div>
                    {l.suspicious && (
                      <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red border border-brand-red px-1.5 py-0.5">FLAGGED</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
